import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
  useGetStudentPerformance,
  useListAssessmentTypes,
  useListGradeScales,
  useListMyPerformance,
} from '../../api/generated/academic/academic';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  AttendanceStatus,
  type AssessmentType,
  type AttendanceTally,
  type ClassRecord,
  type Grade,
  type GradeScale,
  type StudentAttendanceEntry,
  type StudentPerformance as StudentPerformanceModel,
} from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
// A paginacao por cursor ja existe na SPA, e o contrato e o mesmo aqui: cada
// pagina devolve `nextCursor` e nada devolve cursor de volta. Reescrever a pilha
// de cursores nesta tela produziria uma segunda implementacao da mesma regra,
// que e como duas telas passam a discordar sobre o que "voltar" significa.
import { type Pager, usePager } from '../enrollment/AdminOnly';
import styles from './academic.module.css';

/**
 * Desempenho do proprio aluno. Somente leitura: a §3.2 manda a contestacao
 * por suporte, e nenhum campo de edicao existe aqui. Nenhuma mencao ao colega
 * de dupla — o servidor ja filtra, e a tela nao tem o que esconder.
 */
export function StudentPerformance() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  // O tamanho da pagina e do servidor; o que a tela precisa fazer e **seguir o
  // cursor**. Sem isso ela mostraria as aulas mais recentes e calaria sobre o
  // resto, que e exatamente o que `nextCursor` obrigatorio existe para impedir.
  const pager = usePager();
  const performanceQuery = useListMyPerformance(
    pager.cursor === undefined ? {} : { cursor: pager.cursor },
    { query: { enabled: !!me } },
  );

  return (
    <PerformanceView
      title={t('academic.performance.title')}
      performance={performanceQuery.data}
      isPending={performanceQuery.isPending}
      isError={performanceQuery.isError}
      timezone={me?.timezone}
      locale={me?.locale}
      pager={pager}
    >
      <Alert tone="info" title={t('academic.performance.contest')}>
        {t('academic.performance.contestBody')}
      </Alert>
    </PerformanceView>
  );
}

/**
 * Desempenho de **um** aluno, para professor e Super Admin.
 *
 * A tela do aluno existia; o caminho do outro papel nao. Sem ele, quem da aula
 * nao tinha como abrir o historico de nota e registro de quem esta na frente —
 * so o proprio, que e a unica coisa que `listMyPerformance` devolve.
 *
 * **A leitura administrativa nao amplia o que o aluno ve.** O comentario
 * interno continua fora da resposta; o que muda e de quem e o recorte, e nao o
 * que ele contem. E por isso que a mesma apresentacao serve aos dois papeis.
 */
export function StudentPerformanceForStudent() {
  const { t } = useTranslation();
  const { studentId = '' } = useParams();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const pager = usePager();

  const performanceQuery = useGetStudentPerformance(
    studentId,
    pager.cursor === undefined ? {} : { cursor: pager.cursor },
    { query: { enabled: !!me && studentId.length > 0 } },
  );

  const displayName = performanceQuery.data?.student.displayName;

  return (
    <PerformanceView
      title={
        displayName
          ? t('academic.performance.titleFor', { name: displayName })
          : t('academic.performance.title')
      }
      performance={performanceQuery.data}
      isPending={performanceQuery.isPending}
      isError={performanceQuery.isError}
      timezone={me?.timezone}
      locale={me?.locale}
      pager={pager}
    />
  );
}

/** A apresentacao compartilhada pelos dois papeis. */
function PerformanceView({
  title,
  performance,
  isPending,
  isError,
  timezone,
  locale,
  pager,
  children,
}: {
  readonly title: string;
  readonly performance: StudentPerformanceModel | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly timezone: string | undefined;
  readonly locale: string | undefined;
  readonly pager: Pager;
  readonly children?: ReactNode;
}) {
  const { t } = useTranslation();
  const enabled = timezone !== undefined && locale !== undefined;
  const typesQuery = useListAssessmentTypes({ query: { enabled } });
  const scalesQuery = useListGradeScales({ query: { enabled } });

  if (!enabled) return null;

  const types: readonly AssessmentType[] = typesQuery.data ?? [];
  const scales: readonly GradeScale[] = scalesQuery.data ?? [];
  const nextCursor = performance?.nextCursor ?? null;

  return (
    <div className={styles.page}>
      <h1>{title}</h1>

      {isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {!isPending &&
      (performance?.grades.length ?? 0) === 0 &&
      (performance?.records.length ?? 0) === 0 &&
      (performance?.attendance.length ?? 0) === 0 ? (
        <p>{t('academic.performance.empty')}</p>
      ) : null}

      {performance ? <FrequencySection frequency={performance.frequency} /> : null}

      {performance && performance.grades.length > 0 ? (
        <section className={styles.section} aria-labelledby="grades-title">
          <h2 id="grades-title">{t('academic.performance.grades')}</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{t('academic.settings.types')}</th>
                <th scope="col">{t('academic.performance.grades')}</th>
                <th scope="col">{t('academic.settings.scale')}</th>
                <th scope="col">{t('academic.settings.weight')}</th>
              </tr>
            </thead>
            <tbody>
              {performance.grades.map((grade) => (
                <GradeRow key={grade.id} grade={grade} types={types} scales={scales} />
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {performance && performance.records.length > 0 ? (
        <section className={styles.section} aria-labelledby="records-title">
          <h2 id="records-title">{t('academic.performance.records')}</h2>
          <ul className={styles.list}>
            {performance.records.map((record) => (
              <RecordCard key={record.participantId + record.updatedAt} record={record} timezone={timezone} locale={locale} />
            ))}
          </ul>
        </section>
      ) : null}

      {performance && performance.attendance.length > 0 ? (
        <AttendanceSection entries={performance.attendance} />
      ) : null}

      {/*
        A pagina e a **aula**: nota, registro e presenca vem sempre das mesmas
        aulas. Por isso a navegacao e uma so, e nao uma por lista.
      */}
      {performance ? (
        <nav className={styles.section} aria-label={t('academic.performance.pagination')}>
          {nextCursor !== null ? (
            <p className={styles.meta}>{t('academic.performance.pageExplained')}</p>
          ) : null}
          <div className={styles.row}>
            <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
              {t('academic.performance.previousPage')}
            </Button>
            <Button
              disabled={nextCursor === null}
              onClick={() => {
                if (nextCursor !== null) pager.goForward(nextCursor);
              }}
            >
              {t('academic.performance.nextPage')}
            </Button>
          </div>
        </nav>
      ) : null}

      {children}
    </div>
  );
}

/** Os sete contadores da apuracao, e **nenhuma divisao**. */
const FREQUENCY_ROWS = [
  'total',
  'present',
  'late',
  'absent',
  'excused',
  'cancelledBySchool',
] as const;

/**
 * Frequencia e faltas, em contadores.
 *
 * **A tela nao calcula percentual, e a ausencia e a decisao.** Escolher o
 * divisor — se aula cancelada pela escola entra, se falta justificada conta — e
 * regra de negocio da qual sai um numero que a escola usa para decidir
 * aprovacao. Uma divisao escrita aqui seria pior do que uma no servidor, porque
 * ninguem a revisaria; quando a razao for definida, ela chega como campo do
 * contrato.
 *
 * `adjusted` fica **fora** da soma de `total`, e por isso mora numa linha
 * separada: uma linha ajustada ja esta contada no estado efetivo dela, e somar
 * as duas coisas daria um total que nao existe.
 */
function FrequencySection({ frequency }: { readonly frequency: AttendanceTally }) {
  const { t } = useTranslation();

  return (
    <section className={styles.section} aria-labelledby="frequency-title">
      <h2 id="frequency-title">{t('academic.performance.frequency')}</h2>
      <p className={styles.meta}>{t('academic.performance.frequencyExplained')}</p>

      {/*
        Contagem maior que zero, e nao o campo existir: `adjusted` vem sempre no
        corpo, e um aviso preso a presenca do campo apareceria em toda tela.
      */}
      {frequency.adjusted > 0 ? (
        <Alert tone="info">{t('academic.performance.frequencyAdjusted')}</Alert>
      ) : null}

      <table className={styles.table}>
        <tbody>
          {FREQUENCY_ROWS.map((row) => (
            <tr key={row}>
              <th scope="row">{t(`academic.performance.frequencyLabel.${row}`)}</th>
              <td>{frequency[row]}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">{t('academic.performance.frequencyLabel.adjusted')}</th>
            <td>{frequency.adjusted}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

const ATTENDANCE_STATUSES: readonly string[] = Object.values(AttendanceStatus);

/** Rotulo do estado, ou o valor cru quando o contrato ganhar um estado novo. */
function statusLabel(status: string, t: (key: string) => string): string {
  return ATTENDANCE_STATUSES.includes(status) ? t(`attendance.status.${status}`) : status;
}

/**
 * Uma linha por aula, com os tres estados lado a lado.
 *
 * Os tres existem porque dizem coisas diferentes: `computed` e o que o sistema
 * apurou, `adjusted` e a correcao humana, `effective` e qual dos dois vale.
 * Mostrar so o efetivo esconderia que houve ajuste.
 *
 * **Nem autor nem justificativa aparecem, e nao e esquecimento**:
 * `StudentAttendanceEntry` nao os carrega. O aluno le a propria frequencia sem
 * ler o nome de quem corrigiu a linha dele nem o motivo que a pessoa escreveu —
 * isso e trilha de auditoria, e vive em `listOccurrenceAttendance`, que e de
 * Super Admin.
 */
function AttendanceSection({ entries }: { readonly entries: readonly StudentAttendanceEntry[] }) {
  const { t } = useTranslation();

  return (
    <section className={styles.section} aria-labelledby="attendance-title">
      <h2 id="attendance-title">{t('academic.performance.attendance')}</h2>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t('academic.performance.occurrence')}</th>
            <th scope="col">{t('attendance.computed')}</th>
            <th scope="col">{t('attendance.adjusted')}</th>
            <th scope="col">{t('attendance.effective')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.occurrenceId}>
              <th scope="row">{entry.occurrenceId}</th>
              <td>{statusLabel(entry.computed, t)}</td>
              <td>
                {entry.adjusted === null
                  ? t('attendance.noAdjustment')
                  : statusLabel(entry.adjusted, t)}
              </td>
              <td>{statusLabel(entry.effective, t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function GradeRow({
  grade,
  types,
  scales,
}: {
  readonly grade: Grade;
  readonly types: readonly { id: string; label: string }[];
  readonly scales: readonly { id: string; kind: string; minValue: number | null; maxValue: number | null; allowedValues: readonly string[] }[];
}) {
  const { t } = useTranslation();
  const type = types.find((item) => item.id === grade.assessmentTypeId);
  const scale = scales.find((item) => item.id === grade.scaleId);

  return (
    <tr>
      <td>{type?.label ?? grade.assessmentTypeId}</td>
      <td>{renderGradeValue(grade, scale)}</td>
      <td>{scale ? t(`academic.performance.scaleKind.${scale.kind}`) : null}</td>
      <td>{grade.weight}</td>
    </tr>
  );
}

function renderGradeValue(
  grade: Grade,
  scale: { kind: string; minValue: number | null; maxValue: number | null; allowedValues: readonly string[] } | undefined,
): string {
  if (grade.numericValue !== null) {
    if (scale && scale.minValue !== null && scale.maxValue !== null) {
      return `${grade.numericValue} / ${scale.maxValue}`;
    }
    return String(grade.numericValue);
  }
  if (grade.conceptValue !== null) return grade.conceptValue;
  if (grade.rubricValue !== null) return grade.rubricValue;
  return '—';
}

function RecordCard({
  record,
  timezone,
  locale,
}: {
  readonly record: ClassRecord;
  readonly timezone: string;
  readonly locale: string;
}) {
  const { t } = useTranslation();
  return (
    <li className={styles.card}>
      <p className={styles.meta}>
        {formatDateInPersonTimezone(record.updatedAt, timezone, locale)}
        {' · '}
        {record.author.displayName
          ? t('academic.performance.author', { name: record.author.displayName })
          : t('academic.performance.authorOmitted')}
      </p>
      {record.contentTaught ? <p>{record.contentTaught}</p> : null}
      {record.evolution ? <p>{record.evolution}</p> : null}
      {record.visibleComment ? <p>{record.visibleComment}</p> : null}
    </li>
  );
}
